import { StandardEvents } from '@shopify/events';

class CustomChainProduct extends HTMLElement {
  connectedCallback() {
    this.galleryImage = this.querySelector('[data-ccp-main-image]');
    this.galleryButtons = this.querySelectorAll('[data-ccp-thumb]');
    this.galleryMedia = this.querySelector('.ccp__gallery');
    this.galleryContent = this.querySelector('[data-ccp-main-media-content]');
    this.galleryProgress = this.querySelector('[data-ccp-media-progress]');
    this.currentMediaIndex = Math.max(0, Array.from(this.galleryButtons).findIndex((button) => button.getAttribute('aria-current') === 'true'));
    this.sizeDrawer = this.querySelector('[data-ccp-size-drawer]');
    this.advisorDrawer = this.querySelector('[data-ccp-advisor-drawer]');
    this.openSizeButtons = this.querySelectorAll('[data-ccp-open-size]');
    this.closeSizeButtons = this.querySelectorAll('[data-ccp-close-size]');
    this.openAdvisorButtons = this.querySelectorAll('[data-ccp-open-advisor]');
    this.closeAdvisorButtons = this.querySelectorAll('[data-ccp-close-advisor]');
    this.variantInputs = this.querySelectorAll('[data-ccp-size-option]');
    this.variantIdInputs = this.querySelectorAll('[data-ccp-variant-id]');
    this.accordions = this.querySelectorAll('[data-ccp-accordion]');
    this.variantData = this.parseJson('[data-ccp-variants]');
    this.selectedVariant = null;
    this.boundCartUpdate = (event) => this.handleCartUpdate(event);
    this.boundResumeActiveVideo = () => {
      if (!document.hidden) this.syncActiveGalleryMedia();
    };
    this.boundMobileVideoPlay = () => {
      const video = this.galleryContent?.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.play().catch(() => {});
    };
    this.addButtons = this.querySelectorAll('[data-ccp-add-button]');

    this.galleryButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectMedia(button));
    });

    this.updateGalleryProgress();
    this.preloadGalleryVideos();
    this.syncActiveGalleryMedia();
    document.addEventListener('visibilitychange', this.boundResumeActiveVideo);
    window.addEventListener('pageshow', this.boundResumeActiveVideo);
    this.galleryMedia?.addEventListener('touchstart', this.boundMobileVideoPlay, { passive: true });

    if (this.galleryMedia && this.galleryButtons.length > 1) {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isSwiping = false;

      this.galleryMedia.addEventListener('touchstart', (event) => this.handleTouchStart(event), { passive: true });
      this.galleryMedia.addEventListener('touchmove', (event) => this.handleTouchMove(event), { passive: true });
      this.galleryMedia.addEventListener('touchend', (event) => this.handleTouchEnd(event));
    }

    this.openSizeButtons.forEach((button) => {
      button.addEventListener('click', () => this.openDrawer(this.sizeDrawer, 'custom-chain-product-size-open'));
    });

    this.closeSizeButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(button.closest('[data-ccp-size-drawer]'), 'custom-chain-product-size-open'));
    });

    this.openAdvisorButtons.forEach((button) => {
      button.addEventListener('click', () => this.openDrawer(this.advisorDrawer, 'custom-chain-product-advisor-open'));
    });

    this.closeAdvisorButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(button.closest('[data-ccp-advisor-drawer]'), 'custom-chain-product-advisor-open'));
    });

    this.variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.updateVariant(input.value);
        this.closeDrawer(this.sizeDrawer, 'custom-chain-product-size-open');
      });
    });

    this.accordions.forEach((accordion) => {
      const summary = accordion.querySelector('summary');
      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleAccordion(accordion);
      });
    });

    this.addButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!button.disabled) this.setAddToCartLoading(true);
      });
    });

    this.addEventListener('click', (event) => this.handleBackdropClick(event));
    this.addEventListener('keydown', (event) => this.handleKeydown(event));
    document.addEventListener(StandardEvents.cartLinesUpdate, this.boundCartUpdate);

    const checkedSize = this.querySelector('[data-ccp-size-option]:checked')?.value;
    this.updateVariant(checkedSize);
    this.openAdvisorAfterContactSubmit();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.boundCartUpdate);
    document.removeEventListener('visibilitychange', this.boundResumeActiveVideo);
    window.removeEventListener('pageshow', this.boundResumeActiveVideo);
    this.galleryMedia?.removeEventListener('touchstart', this.boundMobileVideoPlay);
    this.preloadedGalleryVideos?.forEach((video) => video.remove());
  }

  parseJson(selector) {
    const script = this.querySelector(selector);
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.isSwiping = false;
  }

  handleTouchMove(event) {
    if (!this.touchStartX) return;

    const deltaX = event.touches[0].clientX - this.touchStartX;
    const deltaY = event.touches[0].clientY - this.touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      this.isSwiping = true;
    }
  }

  handleTouchEnd(event) {
    if (!this.isSwiping) {
      this.touchStartX = 0;
      this.touchStartY = 0;
      return;
    }

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const threshold = 40;

    if (deltaX > threshold) {
      this.swipeGallery(-1);
    } else if (deltaX < -threshold) {
      this.swipeGallery(1);
    }

    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isSwiping = false;
  }

  swipeGallery(direction) {
    const buttons = Array.from(this.galleryButtons);
    if (!buttons.length) return;

    this.currentMediaIndex = (this.currentMediaIndex + direction + buttons.length) % buttons.length;
    this.selectMedia(buttons[this.currentMediaIndex]);
  }

  selectMedia(button, animate = true) {
    const src = button.getAttribute('data-ccp-image');
    const srcset = button.getAttribute('data-ccp-srcset');
    const alt = button.getAttribute('data-ccp-alt') || '';
    const mediaTemplate = button.querySelector('[data-ccp-media-template]');
    const buttons = Array.from(this.galleryButtons);
    const nextIndex = buttons.indexOf(button);
    let direction = nextIndex >= this.currentMediaIndex ? 1 : -1;

    if (this.currentMediaIndex === buttons.length - 1 && nextIndex === 0) direction = 1;
    if (this.currentMediaIndex === 0 && nextIndex === buttons.length - 1) direction = -1;

    if (mediaTemplate && this.galleryContent) {
      if (animate) this.transitionGalleryContent(mediaTemplate, direction);
      else this.replaceGalleryContent(mediaTemplate);
    } else if (animate) this.transitionGalleryImage(src, srcset, alt, direction);
    else this.setGalleryImage(src, srcset, alt);
    this.galleryButtons.forEach((thumb) => thumb.setAttribute('aria-current', 'false'));
    button.setAttribute('aria-current', 'true');

    this.currentMediaIndex = nextIndex;
    this.updateGalleryProgress();
  }

  replaceGalleryContent(mediaTemplate) {
    if (!this.galleryContent || !mediaTemplate) return;
    this.galleryContent.querySelectorAll('video').forEach((video) => video.pause());
    this.galleryContent.replaceChildren(mediaTemplate.content.cloneNode(true));
    this.syncActiveGalleryMedia();
  }

  transitionGalleryContent(mediaTemplate, direction = 1) {
    const canAnimate = window.matchMedia('(max-width: 1199px)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && typeof this.galleryContent?.animate === 'function';
    if (!canAnimate || !this.galleryMedia) return this.replaceGalleryContent(mediaTemplate);

    this.galleryMedia.querySelectorAll('[data-ccp-transition-content]').forEach((content) => content.remove());
    this.galleryContent.getAnimations().forEach((animation) => animation.cancel());
    const outgoing = this.galleryContent.cloneNode(false);
    outgoing.removeAttribute('data-ccp-main-media-content');
    outgoing.setAttribute('data-ccp-transition-content', '');
    outgoing.append(...this.galleryContent.childNodes);
    Object.assign(outgoing.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', zIndex: '1', pointerEvents: 'none' });
    this.galleryMedia.append(outgoing);
    this.galleryContent.replaceChildren(mediaTemplate.content.cloneNode(true));
    this.syncActiveGalleryMedia();
    const offset = direction >= 0 ? 100 : -100;
    const timing = { duration: 360, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
    outgoing.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-offset}%)` }], timing).finished.catch(() => {}).finally(() => {
      outgoing.querySelectorAll('video').forEach((video) => video.pause());
      outgoing.remove();
    });
    this.galleryContent.animate([{ transform: `translateX(${offset}%)` }, { transform: 'translateX(0)' }], timing);
  }

  syncActiveGalleryMedia() {
    this.galleryImage = this.galleryContent?.querySelector('[data-ccp-main-image]') || null;
    const video = this.galleryContent?.querySelector('video');
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const playVideo = () => {
        video.muted = true;
        video.play().catch(() => {});
      };
      video.addEventListener('loadedmetadata', playVideo, { once: true });
      video.addEventListener('loadeddata', playVideo, { once: true });
      video.addEventListener('canplay', playVideo, { once: true });
      if (video.readyState === HTMLMediaElement.HAVE_NOTHING) video.load();
      playVideo();
      requestAnimationFrame(playVideo);
      window.setTimeout(playVideo, 250);
      window.setTimeout(playVideo, 1000);
    }
  }

  preloadGalleryVideos() {
    this.preloadedGalleryVideos = [];
    this.querySelectorAll('template[data-ccp-media-template]').forEach((template) => {
      const sourceVideo = template.content.querySelector('video');
      if (!sourceVideo) return;

      const video = sourceVideo.cloneNode(true);
      video.removeAttribute('autoplay');
      video.removeAttribute('loop');
      video.muted = true;
      video.defaultMuted = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;
      Object.assign(video.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        opacity: '0',
        pointerEvents: 'none',
      });
      this.append(video);
      video.load();
      this.preloadedGalleryVideos.push(video);
    });
  }

  transitionGalleryImage(src, srcset, alt, direction = 1) {
    const canAnimate = window.matchMedia('(max-width: 1199px)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && typeof this.galleryImage?.animate === 'function';

    if (!canAnimate || !this.galleryMedia) {
      this.setGalleryImage(src, srcset, alt);
      return;
    }

    this.galleryMedia.querySelectorAll('[data-ccp-transition-image]').forEach((image) => image.remove());
    this.galleryImage.getAnimations().forEach((animation) => animation.cancel());

    const outgoingImage = this.galleryImage.cloneNode(true);
    outgoingImage.removeAttribute('data-ccp-main-image');
    outgoingImage.setAttribute('data-ccp-transition-image', '');
    outgoingImage.setAttribute('aria-hidden', 'true');
    Object.assign(outgoingImage.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      zIndex: '1',
      pointerEvents: 'none',
      objectFit: 'cover',
    });
    this.galleryMedia.append(outgoingImage);

    this.setGalleryImage(src, srcset, alt);
    const offset = direction >= 0 ? 100 : -100;
    const timing = { duration: 360, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
    outgoingImage.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-offset}%)` }], timing)
      .finished.catch(() => {}).finally(() => outgoingImage.remove());
    this.galleryImage.animate([{ transform: `translateX(${offset}%)` }, { transform: 'translateX(0)' }], timing);
  }

  updateGalleryProgress() {
    if (!this.galleryProgress) return;

    const count = this.galleryButtons.length;
    this.galleryProgress.hidden = count <= 1;
    this.galleryProgress.style.setProperty('--media-progress-count', Math.max(count, 1));
    this.galleryProgress.style.setProperty('--media-progress-index', Math.max(this.currentMediaIndex, 0));
  }

  setGalleryImage(src, srcset, alt) {
    if (!this.galleryImage || !src) return;

    this.galleryImage.setAttribute('src', src);
    if (srcset) this.galleryImage.setAttribute('srcset', srcset);
    this.galleryImage.setAttribute('alt', alt || '');
  }

  openDrawer(drawer, lockClass) {
    if (!drawer) return;

    drawer.setAttribute('open', '');
    document.documentElement.classList.add(lockClass);
    const firstFocusable = drawer.querySelector('input:not([type="hidden"]), textarea, button');
    window.setTimeout(() => firstFocusable?.focus(), 180);
  }

  closeDrawer(drawer, lockClass) {
    if (!drawer) return;

    drawer.removeAttribute('open');
    document.documentElement.classList.remove(lockClass);
  }

  handleBackdropClick(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.matches('.ccp__drawer-backdrop')) {
      const drawer = event.target.closest('[data-ccp-size-drawer], [data-ccp-advisor-drawer]');
      const lockClass = drawer?.hasAttribute('data-ccp-size-drawer')
        ? 'custom-chain-product-size-open'
        : 'custom-chain-product-advisor-open';
      this.closeDrawer(drawer, lockClass);
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;

    this.closeDrawer(this.sizeDrawer, 'custom-chain-product-size-open');
    this.closeDrawer(this.advisorDrawer, 'custom-chain-product-advisor-open');
  }

  updateVariant(size) {
    const selectedVariant = this.getVariantForSize(size);
    if (!selectedVariant) return;

    this.selectedVariant = selectedVariant;
    this.dataset.selectedVariantId = selectedVariant.id;
    this.updateUrl(selectedVariant);

    this.querySelectorAll(`[data-ccp-size-option][value="${this.escapeSelectorValue(selectedVariant.size)}"]`).forEach((input) => {
      input.checked = true;
    });

    this.variantIdInputs.forEach((input) => {
      input.value = selectedVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.querySelectorAll('product-form-component').forEach((form) => {
      form.dataset.currentVariantId = selectedVariant.id;
      form.dataset.quantityDefault = selectedVariant.quantityDefault || '1';
    });

    this.querySelectorAll('[data-ccp-size-title]').forEach((title) => {
      title.textContent = selectedVariant.sizeTitle || selectedVariant.size;
    });

    this.querySelectorAll('[data-ccp-size-subtext]').forEach((subtext) => {
      subtext.textContent = selectedVariant.sizeSubtext || '';
      subtext.toggleAttribute('hidden', !selectedVariant.sizeSubtext);
    });

    this.querySelectorAll('[data-ccp-price]').forEach((price) => {
      price.classList.toggle('ccp__price--sale', Boolean(selectedVariant.onSale));
      price.querySelectorAll('[data-ccp-sale-price]').forEach((salePrice) => {
        salePrice.textContent = selectedVariant.price;
      });
      price.querySelectorAll('[data-ccp-compare-price]').forEach((comparePrice) => {
        comparePrice.textContent = selectedVariant.compareAtPrice || '';
        comparePrice.classList.toggle('hidden', !selectedVariant.onSale);
      });
      price.querySelectorAll('[data-ccp-sale-badge]').forEach((badge) => {
        const customBadgeText = badge.dataset.ccpCustomBadgeText || '';
        const badgeText = !selectedVariant.available
          ? badge.dataset.ccpSoldOutBadgeText || 'SOLD OUT'
          : selectedVariant.onSale
            ? badge.dataset.ccpSaleBadgeText || 'SALE'
            : customBadgeText;
        badge.textContent = badgeText;
        Array.from(badge.classList).forEach((className) => {
          if (className.startsWith('badge-')) badge.classList.remove(className);
        });
        if (badgeText) badge.classList.add(`badge-${badgeText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`);
        badge.classList.toggle('hidden', !badgeText);
      });
    });

    this.querySelectorAll('[data-ccp-drawer-price]').forEach((price) => {
      price.textContent = selectedVariant.price;
    });

    this.updateAdvisorFields(selectedVariant);

    this.addButtons.forEach((button) => {
      button.toggleAttribute('disabled', !selectedVariant.available);
      const text = button.querySelector('.add-to-cart-text__content span span');
      if (text) text.textContent = selectedVariant.available ? button.dataset.availableText : button.dataset.soldOutText;
    });

    this.querySelectorAll('add-to-cart-component').forEach((container) => {
      if (selectedVariant.cartImage) container.dataset.productVariantMedia = selectedVariant.cartImage;
    });

    if (selectedVariant.featuredImage) {
      const matchingThumb = Array.from(this.galleryButtons).find((thumb) => thumb.getAttribute('data-ccp-image') === selectedVariant.featuredImage);
      if (matchingThumb) this.selectMedia(matchingThumb, false);
      else this.setGalleryImage(selectedVariant.featuredImage, selectedVariant.featuredImageSrcset, selectedVariant.featuredImageAlt);
    }
  }

  getVariantForSize(size) {
    if (!this.variantData.length) return null;

    return this.variantData.find((variant) => variant.size === size) || this.variantData[0];
  }

  updateUrl(variant) {
    if (!variant?.id || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateAdvisorFields(variant) {
    const variantTitle = variant.sizeTitle || variant.size || '';
    this.querySelectorAll('[data-ccp-advisor-variant]').forEach((input) => {
      input.value = variantTitle;
    });
    this.querySelectorAll('[data-ccp-advisor-url]').forEach((input) => {
      input.value = window.location.href;
    });
  }

  openAdvisorAfterContactSubmit() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('contact_posted') !== 'true') return;

    this.openDrawer(this.advisorDrawer, 'custom-chain-product-advisor-open');
  }

  escapeSelectorValue(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  toggleAccordion(accordion) {
    if (accordion.open) {
      this.closeAccordion(accordion);
      return;
    }

    this.accordions.forEach((item) => {
      if (item !== accordion) this.closeAccordion(item);
    });
    this.openAccordion(accordion);
  }

  openAccordion(accordion) {
    const panel = accordion.querySelector('.ccp__accordion-panel');
    if (!panel) return;

    accordion.open = true;
    panel.style.maxHeight = '0px';
    window.requestAnimationFrame(() => {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    });
  }

  closeAccordion(accordion) {
    const panel = accordion.querySelector('.ccp__accordion-panel');
    if (!panel || !accordion.open) return;

    panel.style.maxHeight = `${panel.scrollHeight}px`;
    window.requestAnimationFrame(() => {
      panel.style.maxHeight = '0px';
    });

    const handleTransitionEnd = () => {
      accordion.open = false;
      panel.removeEventListener('transitionend', handleTransitionEnd);
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
  }

  handleCartUpdate(event) {
    if (event.action !== 'add') return;

    const productForm = this.querySelector('product-form-component');
    if (event.target !== productForm && event.target !== this && !this.contains(event.target)) return;

    event.promise
      ?.then(({ detail }) => {
        this.setAddToCartLoading(false);
        if (detail?.didError) return;
        this.openCartDrawer();
      })
      .catch(() => {
        this.setAddToCartLoading(false);
      });
  }

  setAddToCartLoading(isLoading) {
    if (this.loadingTimeout) window.clearTimeout(this.loadingTimeout);

    this.addButtons.forEach((button) => {
      button.toggleAttribute('aria-busy', isLoading);
      button.dataset.loading = isLoading ? 'true' : 'false';
    });

    if (isLoading) {
      this.loadingTimeout = window.setTimeout(() => this.setAddToCartLoading(false), 8000);
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('theme-drawer#cart-drawer');

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else if (window.Shopify?.actions?.openCart) {
      window.Shopify.actions.openCart();
    }
  }
}

if (!customElements.get('custom-chain-product')) {
  customElements.define('custom-chain-product', CustomChainProduct);
}
